import {useCallback, useRef} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {config} from '../config';
import {useBankIDClient} from './useBankIDClient';

export type BankIDStatus =
  | 'PENDING'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'UNKNOWN'
  | 'FAILED';

export interface BankIDStatusResponse {
  status: BankIDStatus;
  authId?: string;
  autoStartToken?: string;
  expiresAt?: string;
  // Only present while pending and when the backend's QR rotation toggle is on.
  qrCode?: string;
}

export interface InitializeSessionResponse {
  autoStartToken: string | null;
  authorizeUrl: string | null;
}

export type OidcCallbackRequest =
  | {code: string}
  | {error: string; error_description?: string};

export interface OidcCallbackResponse {
  status: BankIDStatus;
  authId: string;
  authType?: 'OAUTH2' | 'DIRECT' | null;
}

export interface BankIDStatusRequest {
  token: string | undefined | null;
  authToken: string | undefined | null;
  bft?: string | undefined | null;
  statusEnabled?: boolean;
  host?: string;
  // Re-mints authToken when it's close to expiry, so a poll that outlives
  // the token's own (short) lifetime doesn't start 401ing mid-flow.
  refreshAuthToken?: () => Promise<string | undefined>;
}

export const useBankIDStatus = ({
  token,
  authToken,
  bft,
  statusEnabled = true,
  host,
  refreshAuthToken,
}: BankIDStatusRequest) => {
  const client = useBankIDClient(authToken, refreshAuthToken);
  const bankIdHost = host ?? config.bankIdHost;
  const isEnabled =
    statusEnabled && Boolean(token) && Boolean(authToken) && Boolean(bft);

  const query = useQuery({
    queryKey: ['bankid', 'status', token ?? 'none', bft ?? 'none'] as const,
    enabled: isEnabled,
    refetchInterval: isEnabled ? 1000 : false,
    queryFn: async () => {
      if (!token) throw new Error('Token required for BankID status check');
      if (!bft) throw new Error('BFT required for BankID status check');
      const url = `${bankIdHost}/authentication/${token}/status?pollProvider=true&bft=${encodeURIComponent(
        bft,
      )}`;
      // Shorter than the client's 60s default - a stuck poll shouldn't block the 1s refetch
      // cadence for that long. Kept above the backend's 5s BankID collect timeout so its
      // graceful fallback gets a chance to return first.
      return await client.getJSONBody<BankIDStatusResponse>(
        url,
        undefined,
        8000,
      );
    },
  });

  const cancel = useMutation({
    mutationKey: ['bankid', 'cancel', token ?? 'none'] as const,
    mutationFn: async () => {
      if (!token)
        throw new Error('Token required to cancel BankID authentication');
      const url = `${bankIdHost}/authentication/${token}`;
      return await client.fetchJSONBody<{authId: string}>(
        'DELETE',
        url,
        undefined,
      );
    },
  });

  const initializeSession = useMutation({
    mutationKey: ['bankid', 'init', token ?? 'none', bft ?? 'none'] as const,
    mutationFn: async (): Promise<InitializeSessionResponse> => {
      if (!token)
        throw new Error('Token required to initialize BankID authentication');
      if (!bft)
        throw new Error('BFT required to initialize BankID authentication');
      const url = `${bankIdHost}/authentication/${token}/init`;
      // 204: legacy mode (autoStartToken in URL). 200: new mode with body.
      const response = await client.fetchJSONBody<
        {autoStartToken?: string; authorizeUrl?: string} | undefined
      >('POST', url, {bft});
      return {
        autoStartToken: response?.autoStartToken ?? null,
        authorizeUrl: response?.authorizeUrl ?? null,
      };
    },
  });

  const oidcCallback = useMutation({
    mutationKey: ['bankid', 'oidc-callback', token ?? 'none'] as const,
    mutationFn: async (
      request: OidcCallbackRequest,
    ): Promise<OidcCallbackResponse> => {
      if (!token) throw new Error('Token required for OIDC callback');
      if (!bft) throw new Error('BFT required for OIDC callback');
      const url = `${bankIdHost}/authentication/${token}/oidc_callback`;
      return await client.fetchJSONBody<OidcCallbackResponse>('POST', url, {
        bft,
        ...request,
      });
    },
  });

  const initializeSessionRef = useRef(initializeSession.mutateAsync);
  // eslint-disable-next-line react-hooks/refs
  initializeSessionRef.current = initializeSession.mutateAsync;
  const initializeSessionAsync = useCallback(
    () => initializeSessionRef.current(),
    [],
  );

  const createBrowserFixation = useMutation({
    mutationKey: ['bankid', 'browser-fixation', bft ?? 'none'] as const,
    mutationFn: async (authId: string) => {
      if (!authId)
        throw new Error('AuthId required to create browser proof token');
      if (!bft) throw new Error('BFT required to create browser proof token');
      if (host) {
        const url = `${bankIdHost}/authentication/${token}/browser_fixation`;
        return await client.fetchJSONBody<{bpt: string; authId: string}>(
          'POST',
          url,
          {bft},
        );
      }
      const url = `${config.bankIdHost}/authentication/browser_fixation`;
      return await client.fetchJSONBody<{bpt: string; authId: string}>(
        'POST',
        url,
        {authId, bft},
      );
    },
  });

  return {
    query,
    cancel,
    initializeSession,
    initializeSessionAsync,
    oidcCallback,
    createBrowserFixation,
  };
};
